import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { requiredTier, tierNameKey, type Feature } from '@/lib/tiers';
import { openUpgrade } from '@/lib/upgrade';
import { TierIcon } from '@/components/ui/tier-icon';
import { cn } from '@/lib/utils';

/****************
 * A FEATURE THE ACCOUNT'S TIER DOES NOT OPEN. 2026-09-26.
 *
 * Guillaume: "locked features must be visible, just unusable". So a locked
 * feature is drawn as it always is, dimmed, with the icon of the tier that
 * opens it -- never hidden. Touching it opens the plans pop-up ("a call to
 * action ... so they can pay and chose a membership"). Today only an admin's
 * tier lens locks anything (lib/tiers.ts): for every real account `can()` is
 * true.
 ****************/

/** The icon of the tier that opens a feature, as a button to the plans. Nothing when the account has it. */
export function LockedBadge({ feature, className }: { feature: Feature; className?: string }) {
  const { t } = useTranslation('common');
  const { can } = useAuth();
  if (can(feature)) return null;
  const tier = requiredTier(feature);
  const label = t('tier.locked.needs', { tier: t(tierNameKey(tier)) });
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openUpgrade(feature); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openUpgrade(feature); } }}
      className={cn(
        'inline-flex items-center justify-center gap-0.5 rounded-full border border-current/30 bg-white/[0.04]',
        'h-5 px-1.5 shrink-0 cursor-pointer hover:bg-white/[0.08]',
        className,
      )}
      title={label}
      aria-label={label}
    >
      <Lock size={9} weight="bold" className="text-muted-foreground" aria-hidden />
      <TierIcon tier={tier} size={11} />
    </span>
  );
}

/**
 * Its children, usable only if the tier opens `feature`. Otherwise the same
 * children, dimmed and inert (no click, no focus, no keyboard), under a
 * see-through button that opens the plans, with the badge on top.
 */
export function Locked({
  feature, children, className, note = true,
}: { feature: Feature; children: ReactNode; className?: string; note?: boolean }) {
  const { t } = useTranslation('common');
  const { can } = useAuth();
  if (can(feature)) return <>{children}</>;
  const label = t('tier.locked.needs', { tier: t(tierNameKey(requiredTier(feature))) });
  return (
    <div className={cn('relative', className)} data-locked={feature}>
      {/* React 18 has no `inert` prop yet; the attribute is what browsers read. */}
      <div {...({ inert: '' } as object)} aria-disabled="true" className="pointer-events-none select-none opacity-45">
        {children}
      </div>
      <button
        type="button"
        onClick={() => openUpgrade(feature)}
        className="absolute inset-0 cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        aria-label={label}
        title={label}
      />
      {/* On the corner, outside the content, so it never covers its text. */}
      {note && (
        <div className="absolute -top-2.5 -end-2.5 flex items-center gap-1 pointer-events-none">
          <LockedBadge feature={feature} />
        </div>
      )}
    </div>
  );
}

/** A whole section the tier does not open: which tier opens it, and the way to the plans. */
export function LockedPanel({ feature, className }: { feature: Feature; className?: string }) {
  const { t } = useTranslation('common');
  const { can } = useAuth();
  if (can(feature)) return null;
  const req = requiredTier(feature);
  const tier = t(tierNameKey(req));
  return (
    <div
      className={cn('flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm', className)}
      data-locked={feature}
    >
      <TierIcon tier={req} size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-medium">{t('tier.locked.needs', { tier })}</p>
        <p className="text-xs text-muted-foreground">{t('tier.locked.body', { tier })}</p>
      </div>
      <button
        type="button"
        onClick={() => openUpgrade(feature)}
        className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t('upgrade.cta')}
      </button>
    </div>
  );
}
